import { ROLES } from "./constants";

export const APP_ASSIGNABLE_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.CEO,
  ROLES.EXECUTIVE_TEAM,
  ROLES.TASK_FORCE,
  ROLES.POINT_PEOPLE,
  ROLES.TEAM_LEAD_DOCTOR,
  ROLES.TEAM_LEAD_OPTICAL,
  ROLES.TEAM_MEMBER,
] as const;

export type AppAssignableRole = typeof APP_ASSIGNABLE_ROLES[number];

const ROLE_LEVELS: Record<AppAssignableRole, number> = {
  [ROLES.SUPER_ADMIN]: 13,
  [ROLES.CEO]: 12,
  [ROLES.EXECUTIVE_TEAM]: 11,
  [ROLES.TASK_FORCE]: 10,
  [ROLES.POINT_PEOPLE]: 9,
  [ROLES.TEAM_LEAD_DOCTOR]: 8,
  [ROLES.TEAM_LEAD_OPTICAL]: 7,
  [ROLES.TEAM_MEMBER]: 4,
};

const ROLE_LABELS: Record<string, string> = {
  [ROLES.SUPER_ADMIN]: "Super Admin",
  [ROLES.CEO]: "CEO",
  [ROLES.EXECUTIVE_TEAM]: "Executive Team",
  [ROLES.TASK_FORCE]: "Task Force",
  [ROLES.POINT_PEOPLE]: "Point People",
  [ROLES.TEAM_LEAD_DOCTOR]: "Team Lead Doctor",
  [ROLES.TEAM_LEAD_OPTICAL]: "Team Lead Optical",
  [ROLES.TEAM_MEMBER]: "Team Member",
};

const LEGACY_ROLE_ALIASES: Record<string, AppAssignableRole> = {
  User: ROLES.TEAM_MEMBER,
  TeamLead: ROLES.TEAM_LEAD_OPTICAL,
  TeamLeadClinic: ROLES.TEAM_LEAD_OPTICAL,
  TeamLeadOptical: ROLES.TEAM_LEAD_OPTICAL,
  TeamLeadDoctor: ROLES.TEAM_LEAD_DOCTOR,
  Doctor: ROLES.TEAM_LEAD_DOCTOR,
  BoltEyeTaskForce: ROLES.TASK_FORCE,
  ExecutiveTaskforce: ROLES.TASK_FORCE,
  TaskForce: ROLES.TASK_FORCE,
  PointPeople: ROLES.POINT_PEOPLE,
  NextGenExecutiveTeam: ROLES.EXECUTIVE_TEAM,
  ExecutiveVirtualTeam: ROLES.EXECUTIVE_TEAM,
  VirtualExecutive: ROLES.EXECUTIVE_TEAM,
  TeamMember: ROLES.TEAM_MEMBER,
};

function normalizeRaw(role: string): string {
  return String(role || "").trim();
}

export function normalizeRole(role: string | null | undefined): AppAssignableRole | "" {
  const raw = normalizeRaw(role || "");
  if (!raw) return "";
  if ((APP_ASSIGNABLE_ROLES as readonly string[]).includes(raw)) {
    return raw as AppAssignableRole;
  }
  return LEGACY_ROLE_ALIASES[raw] || "";
}

export function isKnownRole(role: string | null | undefined): boolean {
  return !!normalizeRole(role);
}

export function getRoleLabel(role: string | null | undefined): string {
  const normalized = normalizeRole(role);
  if (!normalized) return String(role || "");
  return ROLE_LABELS[normalized] || normalized;
}

export function getRoleLevel(role: string | null | undefined): number {
  const normalized = normalizeRole(role);
  if (!normalized) return 0;
  return ROLE_LEVELS[normalized] || 0;
}

export function isRoleAtLeast(
  actorRole: string | null | undefined,
  minimumRole: string | null | undefined,
): boolean {
  const minimumLevel = getRoleLevel(minimumRole);
  return minimumLevel > 0 && getRoleLevel(actorRole) >= minimumLevel;
}

export function hasExecutiveLocationAccess(
  actorRole: string | null | undefined,
  isSuperAdmin = false,
): boolean {
  return isSuperAdmin || isRoleAtLeast(actorRole, ROLES.EXECUTIVE_TEAM);
}

export function isTeamLeadRole(role: string | null | undefined): boolean {
  const normalized = normalizeRole(role);
  return normalized === ROLES.TEAM_LEAD_DOCTOR || normalized === ROLES.TEAM_LEAD_OPTICAL;
}

export function canManageLocationRole(
  actorRole: string | null | undefined,
  isSuperAdmin = false,
): boolean {
  if (isSuperAdmin) return true;
  const normalized = normalizeRole(actorRole);
  return [
    ROLES.CEO,
    ROLES.EXECUTIVE_TEAM,
    ROLES.TASK_FORCE,
    ROLES.POINT_PEOPLE,
    ROLES.TEAM_LEAD_DOCTOR,
    ROLES.TEAM_LEAD_OPTICAL,
  ].includes(normalized as AppAssignableRole);
}

export function getAssignableRolesForActor(
  actorRole: string | null | undefined,
  isSuperAdmin: boolean,
): AppAssignableRole[] {
  if (isSuperAdmin) return [...APP_ASSIGNABLE_ROLES];

  const normalizedActor = normalizeRole(actorRole);
  if (!normalizedActor) return [];

  switch (normalizedActor) {
    case ROLES.CEO:
    case ROLES.EXECUTIVE_TEAM:
      return [
        ROLES.CEO,
        ROLES.EXECUTIVE_TEAM,
        ROLES.TASK_FORCE,
        ROLES.POINT_PEOPLE,
        ROLES.TEAM_LEAD_DOCTOR,
        ROLES.TEAM_LEAD_OPTICAL,
        ROLES.TEAM_MEMBER,
      ];
    case ROLES.TASK_FORCE:
      return [
        ROLES.TASK_FORCE,
        ROLES.POINT_PEOPLE,
        ROLES.TEAM_LEAD_DOCTOR,
        ROLES.TEAM_LEAD_OPTICAL,
        ROLES.TEAM_MEMBER,
      ];
    case ROLES.POINT_PEOPLE:
      return [
        ROLES.TEAM_LEAD_DOCTOR,
        ROLES.TEAM_LEAD_OPTICAL,
        ROLES.TEAM_MEMBER,
      ];
    case ROLES.TEAM_LEAD_DOCTOR:
    case ROLES.TEAM_LEAD_OPTICAL:
      return [ROLES.TEAM_MEMBER];
    default:
      return [];
  }
}

export function canCreateUsers(
  actorRole: string | null | undefined,
  isSuperAdmin: boolean,
): boolean {
  return getAssignableRolesForActor(actorRole, isSuperAdmin).length > 0;
}

export function canAssignRole(
  actorRole: string | null | undefined,
  requestedRole: string | null | undefined,
  isSuperAdmin: boolean,
): boolean {
  const normalizedRequested = normalizeRole(requestedRole);
  if (!normalizedRequested) return false;
  const assignable = getAssignableRolesForActor(actorRole, isSuperAdmin);
  return assignable.includes(normalizedRequested);
}
